import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useGameStore } from "../../store/gameStore";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Dices, MessageCircleWarning } from "lucide-react";
import TriggerBadge from "./TriggerBadge";

export default function TemplateTriggerModal({ gameId, character, myMemberId, myMemberName }) {
  const pending = useGameStore((s) => s.pendingTemplateTrigger);
  const setPending = useGameStore((s) => s.setPendingTemplateTrigger);
  const sendWs = useGameStore((s) => s.sendWs);
  const [stats, setStats] = useState(null);
  const [rolling, setRolling] = useState(false);
  const [result, setResult] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [talkedToGm, setTalkedToGm] = useState(false);

  const isMine = pending && pending.member_id === myMemberId;

  useEffect(() => {
    setAcknowledged(false);
    setTalkedToGm(false);
    if (isMine && character?.id) {
      api.get(`/characters/${character.id}/stats`).then((r) => setStats(r.data)).catch(() => setStats(null));
    }
  }, [pending?.template_id]);

  if (!isMine) return null;

  const close = () => {
    setPending(null);
    setResult(null);
    setStats(null);
    setAcknowledged(false);
    setTalkedToGm(false);
  };

  const talkToGm = (rollInfo) => {
    sendWs({ type: "talk_to_gm_ping", data: { member_id: myMemberId, member_name: myMemberName, label: pending.label, roll_result: rollInfo || null } });
    setTalkedToGm(true);
    toast.success("The GM has been notified — they'll narrate what happens next.");
  };

  const doRoll = async () => {
    if (!character?.id) {
      toast.error("Create a character first to roll");
      return;
    }
    setRolling(true);
    try {
      const checkKind = pending.check_kind || "save";
      let modifier = 0;
      let rollLabel = `${pending.label} (${pending.save_type} save)`;
      if (checkKind === "skill") {
        const skillMod = stats?.skills?.[pending.skill]?.modifier;
        modifier = typeof skillMod === "number" ? skillMod : 0;
        rollLabel = `${pending.label} (${pending.skill} check)`;
      } else if (checkKind === "flat") {
        modifier = 0;
        rollLabel = `${pending.label} (flat check)`;
      } else {
        const saveMod = stats?.saves?.[pending.save_type]?.modifier;
        modifier = typeof saveMod === "number" ? saveMod : 0;
      }
      const { data } = await api.post(`/characters/${character.id}/roll`, { game_id: gameId, label: rollLabel, modifier });
      const total = data.roll_breakdown.total;
      const passed = total >= pending.dc;
      const { data: resolveData } = await api.post(`/games/${gameId}/templates/${pending.template_id}/resolve`, { passed, token_id: pending.token_id });
      setResult({ total, pass: passed, effect: resolveData?.effect_result });
    } catch {
      toast.error("Roll failed");
    } finally {
      setRolling(false);
    }
  };

  return (
    <>
      {!acknowledged && (
        <TriggerBadge testId="template-trigger-badge" label={pending.label} onClick={() => setAcknowledged(true)} />
      )}
      <Dialog open={acknowledged} onOpenChange={(v) => !v && close()}>
        <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5] max-w-sm" data-testid="template-trigger-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{pending.label}</DialogTitle>
            <DialogDescription className="capitalize">
              {pending.check_kind === "skill" ? `Roll your ${pending.skill} check to beat DC ${pending.dc}` :
               pending.check_kind === "flat" ? `Roll a flat check (no modifier) to beat DC ${pending.dc}` :
               `Roll your ${pending.save_type} save to beat DC ${pending.dc}`}
            </DialogDescription>
          </DialogHeader>
          {result ? (
            <div className="space-y-3">
              <p className={`text-2xl font-display ${result.pass ? "text-[#4d7c0f]" : "text-[#D90429]"}`} data-testid="template-roll-result">
                Rolled {result.total} — {result.pass ? "Saved!" : "Failed!"}
              </p>
              {result.effect?.message && (
                <p data-testid="template-effect-message" className="text-sm italic text-[#f1e0c5]/80 bg-black/30 border border-white/10 rounded-lg p-2">
                  {result.effect.message}
                </p>
              )}
              {result.effect?.damage && (
                <p data-testid="template-effect-damage" className="text-sm text-[#D90429]">
                  Took {result.effect.damage.final} {result.effect.damage.type || ""} damage
                  {result.effect.damage.note ? ` (${result.effect.damage.note}, rolled ${result.effect.damage.raw})` : ""}
                  {result.effect.damage.hp_after != null ? ` — HP now ${result.effect.damage.hp_after}` : ""}
                </p>
              )}
              {result.effect?.conditions_applied?.length > 0 && (
                <p data-testid="template-effect-conditions" className="text-xs text-[#D4AF37]">
                  Conditions applied: {result.effect.conditions_applied.join(", ")}
                </p>
              )}
              <div className="flex gap-2">
                <Button data-testid="template-okay-button" onClick={close} className="flex-1 rounded-full bg-[#D90429] hover:bg-[#b8031f]">Okay</Button>
                <Button data-testid="template-talk-to-gm-after-roll-button" onClick={() => talkToGm({ total: result.total, pass: result.pass })} variant="secondary" className="flex-1 rounded-full gap-2">
                  <MessageCircleWarning className="w-4 h-4" /> Talk to GM
                </Button>
              </div>
            </div>
          ) : talkedToGm ? (
            <div className="space-y-3 text-center py-2">
              <MessageCircleWarning className="w-8 h-8 text-[#D4AF37] mx-auto animate-pulse" />
              <p className="text-sm text-[#f1e0c5]/70">Waiting on the GM to narrate what happens…</p>
              <Button data-testid="template-close-button" onClick={close} variant="secondary" className="w-full rounded-full">Close</Button>
            </div>
          ) : (
            <div className="space-y-2">
              {!character?.id && <p className="text-xs text-[#f1e0c5]/50">Create a character to roll for this save.</p>}
              <Button data-testid="template-roll-button" onClick={doRoll} disabled={rolling || !character?.id} className="w-full rounded-full gap-2 bg-[#D90429] hover:bg-[#b8031f]">
                <Dices className="w-4 h-4" /> Roll Save
              </Button>
              <Button data-testid="template-talk-to-gm-button" onClick={() => talkToGm()} disabled={rolling} variant="secondary" className="w-full rounded-full gap-2">
                <MessageCircleWarning className="w-4 h-4" /> Talk to GM
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
